import { Component, OnDestroy, OnInit } from '@angular/core';
import { ProductService } from '../shared/services/product.service';
import { DownloadService } from '../shared/services/html-png/html-png.service';
import { ToastService } from '../shared/services/toast.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-supplier-summary-export',
  templateUrl: './supplier-summary-export.component.html',
  styleUrls: ['./supplier-summary-export.component.css']
})
export class SupplierSummaryExportComponent implements OnInit, OnDestroy {
  public reportStatus: any = { status: 'not_generated' };
  public supplierRows: any[] = [];
  public visibleSupplierRows: any[] = [];
  public pageIndex = 0;
  public pageSize = 25;
  public pageSizeOptions = [10, 25, 50, 100];
  public elapsedSeconds: number | null = null;
  public isStarting = false;
  private statusTimer: any;
  private elapsedTimer: any;
  private statusRequest: any;
  private startRequest: any;
  private cancelRequest: any;
  private shouldAutoDownload = false;
  private downloadedRunId = '';
  private isDestroyed = false;

  constructor(
    public productServiceObj: ProductService,
    public downloadServiceObj: DownloadService,
    public toastService: ToastService
  ) {}

  ngOnInit() {
    this.refreshStatus();
  }

  isRunningStatus(status) {
    return ['queued', 'waiting', 'processing', 'retrying', 'creating_excel', 'publishing', 'cancelling'].indexOf(status) !== -1;
  }

  generateSupplierSummary() {
    if (this.isStarting || this.isRunningStatus(this.reportStatus.status)) {
      return;
    }

    this.cleanupTrackingRequests();
    this.isStarting = true;
    this.shouldAutoDownload = true;
    this.elapsedSeconds = 0;
    this.reportStatus = { status: 'queued', stage: 'queued', startedAt: new Date().toISOString() };
    this.startElapsedTimer();
    this.startRequest = this.productServiceObj.exportSupplierSummary().subscribe(
      (response: any) => {
        this.startRequest = null;
        this.isStarting = false;
        this.applyStatus(response);
      },
      (error) => {
        this.startRequest = null;
        this.isStarting = false;
        this.shouldAutoDownload = false;
        this.stopElapsedTimer();
        this.reportStatus = {
          status: 'failed',
          stage: 'queued',
          startedAt: this.reportStatus.startedAt,
          failedAt: new Date().toISOString(),
          message: 'Unable to start Supplier Summary generation'
        };
        this.updateElapsedTime();
        this.toastService.error(error && error.error && error.error.message ?
          error.error.message : 'Unable to start Supplier Summary generation');
      }
    );
  }

  cancelSupplierSummary() {
    if (!this.isRunningStatus(this.reportStatus.status) || this.reportStatus.status === 'cancelling' || this.cancelRequest) {
      return;
    }

    this.reportStatus = Object.assign({}, this.reportStatus, {
      status: 'cancelling',
      stage: 'cancelling'
    });
    this.cancelRequest = this.productServiceObj.cancelSupplierSummary().subscribe(
      (response: any) => {
        this.cancelRequest = null;
        this.applyStatus(response);
      },
      (error) => {
        this.cancelRequest = null;
        this.refreshStatus();
        this.toastService.error(error && error.error && error.error.message ?
          error.error.message : 'Unable to cancel Supplier Summary generation');
      }
    );
  }

  refreshStatus() {
    if (this.isDestroyed || this.statusRequest) {
      return;
    }

    this.statusRequest = this.productServiceObj.getSupplierSummaryExportStatus().subscribe(
      (response: any) => {
        this.statusRequest = null;
        this.applyStatus(response);
      },
      (error) => {
        this.statusRequest = null;
        this.stopStatusPolling();
        this.stopElapsedTimer();
        this.toastService.error(error && error.error && error.error.message ?
          error.error.message : 'Unable to check Supplier Summary generation status');
      }
    );
  }

  private applyStatus(response: any) {
    if (this.isDestroyed || !response) {
      return;
    }

    this.reportStatus = response;
    this.isStarting = false;
    if (Array.isArray(response.rows)) {
      this.setSupplierRows(response.rows);
    } else if (response.previousReport && Array.isArray(response.previousReport.rows)) {
      this.setSupplierRows(response.previousReport.rows);
    }

    this.updateElapsedTime();
    if (this.isRunningStatus(response.status)) {
      this.startElapsedTimer();
      this.scheduleStatusRefresh();
      return;
    }

    this.stopStatusPolling();
    this.stopElapsedTimer();
    if (response.status === 'completed' && response.fileUrl && this.shouldAutoDownload) {
      this.downloadReport(response);
    }
  }

  private scheduleStatusRefresh() {
    if (this.statusTimer || this.isDestroyed) {
      return;
    }

    this.statusTimer = setTimeout(() => {
      this.statusTimer = null;
      this.refreshStatus();
    }, 5000);
  }

  private stopStatusPolling() {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }
  }

  private setSupplierRows(rows: any[]) {
    this.supplierRows = rows;
    if (this.pageIndex * this.pageSize >= this.supplierRows.length) {
      this.pageIndex = 0;
    }
    this.updateVisibleSupplierRows();
  }

  onSupplierSummaryPageChange(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateVisibleSupplierRows();
  }

  private updateVisibleSupplierRows() {
    const start = this.pageIndex * this.pageSize;
    this.visibleSupplierRows = this.supplierRows.slice(start, start + this.pageSize);
  }

  private startElapsedTimer() {
    if (this.elapsedTimer || this.isDestroyed || !this.isRunningStatus(this.reportStatus.status)) {
      return;
    }

    this.updateElapsedTime();
    this.elapsedTimer = setInterval(() => this.updateElapsedTime(), 1000);
  }

  private stopElapsedTimer() {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
  }

  private updateElapsedTime() {
    const status = this.reportStatus && this.reportStatus.status;
    const storedElapsedSeconds = this.getStoredElapsedSeconds();

    if (this.isRunningStatus(status)) {
      const startedAt = this.getTimestamp(this.reportStatus.startedAt);
      this.elapsedSeconds = startedAt === null ? null : Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
      return;
    }

    if (storedElapsedSeconds !== null) {
      this.elapsedSeconds = storedElapsedSeconds;
      return;
    }

    const startedAt = this.getTimestamp(this.reportStatus.startedAt);
    let terminalAt: number | null = null;
    if (status === 'completed') {
      terminalAt = this.getTimestamp(this.reportStatus.completedAt || this.reportStatus.generatedAt);
    } else if (status === 'failed') {
      terminalAt = this.getTimestamp(this.reportStatus.failedAt);
    } else if (status === 'cancelled') {
      terminalAt = this.getTimestamp(this.reportStatus.cancelledAt);
    }

    this.elapsedSeconds = startedAt !== null && terminalAt !== null && terminalAt >= startedAt ?
      Math.floor((terminalAt - startedAt) / 1000) : null;
  }

  hasElapsedTime() {
    return this.elapsedSeconds !== null;
  }

  private getStoredElapsedSeconds(): number | null {
    const value = this.reportStatus && this.reportStatus.elapsedSeconds;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const elapsed = Number(value);
    return isFinite(elapsed) && elapsed >= 0 ? Math.floor(elapsed) : null;
  }

  private getTimestamp(value: any): number | null {
    if (!value) {
      return null;
    }
    const timestamp = new Date(value).getTime();
    return isNaN(timestamp) ? null : timestamp;
  }

  private cleanupTrackingRequests() {
    this.stopStatusPolling();
    this.stopElapsedTimer();
    if (this.statusRequest) {
      this.statusRequest.unsubscribe();
      this.statusRequest = null;
    }
    if (this.startRequest) {
      this.startRequest.unsubscribe();
      this.startRequest = null;
    }
    if (this.cancelRequest) {
      this.cancelRequest.unsubscribe();
      this.cancelRequest = null;
    }
  }

  downloadReport(report, forceDownload?) {
    const reportId = report.runId || report.generatedAt || report.fileUrl;
    if (!forceDownload && this.downloadedRunId === reportId) {
      return;
    }

    this.downloadedRunId = reportId;
    this.downloadServiceObj.downloadExcel(
      'ALL_Suppliers_Summary.xlsx',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      environment.apiEndpoint + report.fileUrl
    );
  }

  downloadPreviousReport() {
    if (this.reportStatus.previousReport) {
      this.downloadReport(this.reportStatus.previousReport, true);
    }
  }

  getStatusMessage() {
    const stages: any = {
      queued: 'Waiting to generate...',
      waiting: 'Waiting to generate...',
      connecting_to_database: 'Generating Supplier Summary...',
      aggregating_supplier_summary: 'Generating Supplier Summary...',
      processing: 'Generating Supplier Summary...',
      retrying: 'Retrying Supplier Summary...',
      creating_excel: 'Creating Excel workbook...',
      publishing: 'Finalizing report...',
      cancelling: 'Cancelling Supplier Summary...',
      completed: 'Report is ready.',
      failed: 'Supplier Summary generation failed. Kindly run again.',
      cancelled: 'Generation cancelled.'
    };

    if (['completed', 'failed', 'cancelled'].indexOf(this.reportStatus.status) !== -1) {
      return stages[this.reportStatus.status];
    }
    return stages[this.reportStatus.stage] || stages[this.reportStatus.status] || '';
  }

  getElapsedTime() {
    if (this.elapsedSeconds === null) {
      return '';
    }
    const minutes = Math.floor(this.elapsedSeconds / 60);
    const seconds = this.elapsedSeconds % 60;

    return minutes + 'm ' + ('0' + seconds).slice(-2) + 's';
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.cleanupTrackingRequests();
  }
}
